import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ChartFacade,
  ChartId,
} from '@principle-theorem/ng-clinical-charting/store';
import { BaseMenuComponent } from '@principle-theorem/ng-core';
import {
  BasicDialogService,
  DialogPresets,
  TrackByFunctions,
} from '@principle-theorem/ng-shared';
import {
  ChartedSurface,
  isSameToothRef,
  TreatmentStep,
} from '@principle-theorem/principle-core';
import {
  IChartedTreatment,
  isChartedTreatment,
  isKnownChartableSurface,
  ITreatmentStep,
  type IChartedItemDetail,
  type IChartedRef,
  type IDentalChartViewSurface,
  type ITooth,
} from '@principle-theorem/principle-core/interfaces';
import {
  getError,
  isSameRef,
  multiFilter,
  shareReplayCold,
  snapshot,
  asyncForEach,
  Firestore,
  DocumentReference,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { ReplaySubject, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ChartedToothSurfaceUpdateDialogComponent,
  IChartedToothSurfaceUpdateDialogData,
  IChartedToothSurfaceUpdateDialogResponse,
} from '../../../charted-tooth-surface-update-dialog/charted-tooth-surface-update-dialog.component';
import { CHART_ENTITY_ID } from '../../chart-entity-id';
import { ROOT_OPTIONS } from '../../models/chart-tooth-surfaces-svg-map';
import { FixMissingChartedItemSurfaces } from './fix-missing-charted-item-surfaces';

@Component({
  selector: 'pr-chart-tooth-menu',
  templateUrl: './chart-tooth-menu.component.html',
  styleUrls: ['./chart-tooth-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'prChartToothMenu',
  standalone: false,
})
export class ChartToothMenuComponent extends BaseMenuComponent {
  private _chartId: ChartId = inject(CHART_ENTITY_ID);
  trackByRoot = TrackByFunctions.variable<number>();
  tooth$: ReplaySubject<ITooth> = new ReplaySubject(1);
  rootOptions: number[] = ROOT_OPTIONS;
  chartedItems$: Observable<IChartedItemDetail[]>;
  missingSurfaceChartedItems$: Observable<IChartedItemDetail[]>;
  view$: ReplaySubject<IDentalChartViewSurface> = new ReplaySubject(1);
  disabled$: Observable<boolean>;

  constructor(
    private _snackBar: MatSnackBar,
    private _chartStore: ChartFacade,
    private _dialog: BasicDialogService
  ) {
    super();

    this.chartedItems$ = this.view$.pipe(
      map((view: IDentalChartViewSurface) => view.chartedItems),
      shareReplayCold()
    );

    this.disabled$ = this._chartStore
      .canEdit$(this._chartId)
      .pipe(map((canStore) => !canStore));

    this.missingSurfaceChartedItems$ = this.chartedItems$.pipe(
      multiFilter(
        (chartedItem) =>
          !!chartedItem.item.chartedSurfaces.length &&
          chartedItem.item.chartedSurfaces.some(
            (chartedSurface) =>
              !isKnownChartableSurface(chartedSurface.chartedRef)
          )
      )
    );
  }

  @Input()
  set tooth(tooth: ITooth) {
    this.tooth$.next(tooth);
  }

  @Input()
  set view(view: IDentalChartViewSurface) {
    this.view$.next(view);
  }

  async changeRoots(roots: number, tooth: ITooth): Promise<void> {
    const matchingTeeth = await this._getSelectedTeeth(tooth);

    matchingTeeth.map((matchingTooth) =>
      this._chartStore.updateToothRoots(
        this._chartId,
        matchingTooth.toothRef,
        roots
      )
    );
    this._snackBar.open(`Updated Roots to ${roots}`);
  }

  async removeTooth(tooth: ITooth): Promise<void> {
    const matchingTeeth = await this._getSelectedTeeth(tooth);

    matchingTeeth.map((matchingTooth) =>
      this._chartStore.removeTooth(this._chartId, matchingTooth.toothRef)
    );

    const state = await snapshot(
      this._chartStore.selectedSurfacesState$(this._chartId)
    );
    state.map((matchingTooth) =>
      this._chartStore.deselectSurface(this._chartId, matchingTooth)
    );

    const toothLabel = matchingTeeth.length > 1 ? 'Teeth' : 'Tooth';
    this._snackBar.open(`${toothLabel} Removed`);
    const menu = await snapshot(this.menu$);
    menu.closed.next('click');
  }

  async removeChartedItem(itemDetail: IChartedItemDetail): Promise<void> {
    const selectedSurfaces = await snapshot(
      this._chartStore.selectedSurfacesState$(this._chartId)
    );
    if (!selectedSurfaces.length) {
      await this._chartStore.removeChartedItem(
        this._chartId,
        await snapshot(this.view$),
        itemDetail
      );
      this._snackBar.open(`Removed ${itemDetail.config.name}`);
      return;
    }

    if (isChartedTreatment(itemDetail.item) && itemDetail.stepRef) {
      await this._handleChartedTreatmentRemoval(
        itemDetail.item,
        itemDetail.stepRef,
        selectedSurfaces
      );
      return;
    }

    const chartedItems = await snapshot(
      this._chartStore.chartedItems$(this._chartId)
    );

    const matchingTeeth = await this._getSelectedTeeth(
      await snapshot(this.tooth$)
    );
    const matchingChartedSurfaces = chartedItems
      .filter((chartedItem) =>
        isSameRef(chartedItem.config.ref, itemDetail.config.ref)
      )
      .map((chartedItem) => {
        return chartedItem.chartedSurfaces.filter((chartedSurface) => {
          return matchingTeeth.some(
            (matchingTooth) =>
              chartedSurface.chartedRef.tooth &&
              isSameToothRef(
                matchingTooth.toothRef,
                chartedSurface.chartedRef.tooth
              )
          );
        });
      })
      .flat();

    const matchingChartedRefs = matchingChartedSurfaces.map(
      (chartedSurface) => ({
        ...chartedSurface.chartedRef,
      })
    );

    const selectedChartedRefs = await asyncForEach(
      matchingChartedRefs,
      async (ref) => {
        const isSelected = await snapshot(
          this._chartStore.isSelectedSurface$(this._chartId, ref)
        );
        return isSelected ? ref : undefined;
      }
    );

    await this._chartStore.removeChartedSurfaces(
      this._chartId,
      compact(selectedChartedRefs),
      itemDetail.item
    );

    this._snackBar.open(`Removed ${itemDetail.config.name}`);
  }

  async fixMissingChartedItemSurfaces(
    itemDetail: IChartedItemDetail
  ): Promise<void> {
    const tooth = await snapshot(this.tooth$);

    const fixer = new FixMissingChartedItemSurfaces(
      itemDetail,
      tooth,
      this._chartStore,
      this._chartId
    );

    const surfaces = fixer.getSurfacesForTooth();

    const data: IChartedToothSurfaceUpdateDialogData = {
      chartedItemConfiguration: itemDetail.config,
      tooth,
      surfaces,
      selectableSurfaces: itemDetail.config.availableSurfaces,
    };

    const response = await this._dialog
      .open<
        ChartedToothSurfaceUpdateDialogComponent,
        IChartedToothSurfaceUpdateDialogData,
        IChartedToothSurfaceUpdateDialogResponse
      >(ChartedToothSurfaceUpdateDialogComponent, DialogPresets.flex({ data }))
      .afterClosed()
      .toPromise();

    if (!response?.selectedSurfaces?.length) {
      return;
    }

    const updatedChartedRefs = await fixer.buildChartedRefsFromSelectedSurfaces(
      response.selectedSurfaces
    );

    try {
      await fixer.updateChartedItemSurfaces(updatedChartedRefs);
    } catch (error) {
      this._snackBar.open(`Failed to update surfaces: ${getError(error)}`);
      return;
    }
    this._snackBar.open(`Sufaces fixed for ${itemDetail.config.name}`);
  }

  private async _getSelectedTeeth(tooth: ITooth): Promise<ITooth[]> {
    const selectedSurfaces = await snapshot(
      this._chartStore.selectedSurfacesState$(this._chartId)
    );
    const chartedTeeth = compact(
      selectedSurfaces.flatMap((surface) => surface.tooth)
    );
    const matchingTeeth = await snapshot(
      this._chartStore
        .clinicalChartState$(this._chartId)
        .pipe(
          map((chart) =>
            chart.teeth.filter((matchingTooth) =>
              chartedTeeth.some((chartedTooth) =>
                isSameToothRef(chartedTooth, matchingTooth.toothRef)
              )
            )
          )
        )
    );

    if (!matchingTeeth.length) {
      return [tooth];
    }

    return matchingTeeth;
  }

  private async _handleChartedTreatmentRemoval(
    item: IChartedTreatment,
    stepRef: DocumentReference<ITreatmentStep>,
    selectedSurfaces: Partial<IChartedRef>[]
  ): Promise<void> {
    const step = await Firestore.getDoc(stepRef);
    if (TreatmentStep.isComplete(step)) {
      return;
    }

    step.treatments = compact(
      step.treatments.map((treatment) => {
        if (!isSameRef(treatment.config, item.config)) {
          return treatment;
        }

        const remainingSurfaces = ChartedSurface.filterSurfaceRefs(
          treatment.chartedSurfaces,
          selectedSurfaces
        );
        if (remainingSurfaces.length === treatment.chartedSurfaces.length) {
          return treatment;
        }
        if (!remainingSurfaces.length) {
          return;
        }
        treatment.chartedSurfaces = remainingSurfaces;
        return treatment;
      })
    );

    await Firestore.patchDoc(stepRef, step);
    this._snackBar.open(`Removed ${item.config.name}`);
  }
}
